import { useEffect, useState } from 'react';

import { client, useErrorNotifications } from 'core/http';
import { VehicleFiltersSchema } from 'core/schemas/vehicleFilters';
import { Vehicle } from 'core/types/vehicle';

import usePagination, { PaginationResponse } from './usePagination';

type Params = VehicleFiltersSchema & { cursor?: string };

export default function useVehicles(searchParams: VehicleFiltersSchema) {
  const showErrorNotification = useErrorNotifications();
  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [params, setParams] = useState<Params>(searchParams);
  const { setResponse, cursor, loadMore, hasMore } = usePagination();

  // Update the cursor param on change
  useEffect(() => {
    cursor && setParams(params => ({ ...params, next_cursor: cursor }));
  }, [cursor]);
  // Clear the params and set new given search params (without the prev cursor)
  useEffect(() => setParams(searchParams), [searchParams]);
  // Clear current set of vehicles
  useEffect(() => setVehicles([]), [searchParams]);
  // And remove the cached previous response from the pagination
  useEffect(() => setResponse(undefined), [searchParams, setResponse]);

  useEffect(() => {
    setLoading(true);

    client()
      .get<PaginationResponse<Vehicle>>('vehicles', { params })
      .then(response => response.data)
      .then(response => {
        setResponse(response);
        setVehicles(vehicles => [...vehicles, ...response.data]);
      })
      .catch(showErrorNotification)
      .finally(() => setLoading(false));
  }, [params, setResponse, showErrorNotification]);

  return { vehicles, loading, loadMore, hasMore };
}
